import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Badge,
  Button,
  Form,
  Modal,
} from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { dayOffApplicationsApiService } from '../../services/apiServices';
import { DayOffApplicationResponse, DayOffApplicationStatus } from '../../api-client';
import {
  getDayOffApplicationStatusLabel,
  getDayOffApplicationStatusBadgeVariant,
  getDayOffApplicationTypeLabel,
  getDayOffApplicationTypeBadgeVariant,
  dayOffStatusAtLeast,
} from '../utils/statusConverters';

const MyDayOffDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<DayOffApplicationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const [downloadingOrder, setDownloadingOrder] = useState(false);
  const [downloadingSignedOrder, setDownloadingSignedOrder] = useState(false);
  const [orderFileError, setOrderFileError] = useState<string | null>(null);

  const [signedOrderFile, setSignedOrderFile] = useState<File | null>(null);
  const [uploadingSignedOrder, setUploadingSignedOrder] = useState(false);
  const [uploadSignedOrderError, setUploadSignedOrderError] = useState<string | null>(null);
  const [uploadSignedOrderSuccess, setUploadSignedOrderSuccess] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!applicationId) {
      navigate('/vacation/day-off/my');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await dayOffApplicationsApiService.getMyApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const handleDownloadFile = async () => {
    if (!application || !applicationId) return;

    try {
      setDownloadingFile(true);
      setFileError(null);

      const response = await dayOffApplicationsApiService.getMyApplicationFileUrl(applicationId);
      const fileUrl = response.data?.file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на файл');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setFileError(err.message || 'Ошибка при скачивании файла');
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleDownloadOrder = async () => {
    if (!applicationId) return;

    try {
      setDownloadingOrder(true);
      setOrderFileError(null);

      const response = await dayOffApplicationsApiService.getMyOrderUrl(applicationId);
      const fileUrl = response.data?.file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на приказ');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setOrderFileError(err.message || 'Ошибка при скачивании приказа');
    } finally {
      setDownloadingOrder(false);
    }
  };

  const handleDownloadSignedOrder = async () => {
    if (!applicationId) return;

    try {
      setDownloadingSignedOrder(true);
      setOrderFileError(null);

      const response = await dayOffApplicationsApiService.getMySignedOrderUrl(applicationId);
      const fileUrl = response.data?.file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на подписанный приказ');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setOrderFileError(err.message || 'Ошибка при скачивании подписанного приказа');
    } finally {
      setDownloadingSignedOrder(false);
    }
  };

  const handleUploadSignedOrder = async () => {
    if (!applicationId || !signedOrderFile) return;

    try {
      setUploadingSignedOrder(true);
      setUploadSignedOrderError(null);
      setUploadSignedOrderSuccess(false);

      const tempFileResponse = await dayOffApplicationsApiService.uploadTempFile(signedOrderFile);
      const fileUrl = tempFileResponse.data?.file_url;
      if (!fileUrl) {
        throw new Error('Не удалось загрузить файл');
      }

      await dayOffApplicationsApiService.uploadMySignedOrder(
        applicationId,
        fileUrl,
        signedOrderFile.name
      );

      const updatedResponse = await dayOffApplicationsApiService.getMyApplication(applicationId);
      setApplication(updatedResponse.data || null);
      setUploadSignedOrderSuccess(true);
      setSignedOrderFile(null);
    } catch (err: any) {
      setUploadSignedOrderError(err.message || 'Ошибка при загрузке подписанного приказа');
    } finally {
      setUploadingSignedOrder(false);
    }
  };

  const reloadApplication = async () => {
    if (!applicationId) return;
    try {
      const response = await dayOffApplicationsApiService.getMyApplication(applicationId);
      setApplication(response.data || null);
    } catch {}
  };

  const handleConfirm = async () => {
    if (!applicationId) return;

    try {
      setActionLoading(true);
      setActionError(null);
      await dayOffApplicationsApiService.confirmMyDayOff(applicationId);
      await reloadApplication();
      setShowConfirmModal(false);
    } catch (err: any) {
      setActionError(err.message || 'Ошибка при подтверждении выходного');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!applicationId) return;

    try {
      setActionLoading(true);
      setActionError(null);
      await dayOffApplicationsApiService.cancelMyDayOff(applicationId);
      await reloadApplication();
      setShowCancelModal(false);
    } catch (err: any) {
      setActionError(err.message || 'Ошибка при отмене выходного');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/vacation/day-off/my')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявка на выходной</h2>
            <Link to="/vacation/day-off/my">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          {fileError && <Alert variant="warning">{fileError}</Alert>}
          {orderFileError && <Alert variant="warning">{orderFileError}</Alert>}
          {uploadSignedOrderError && <Alert variant="danger">{uploadSignedOrderError}</Alert>}
          {uploadSignedOrderSuccess && (
            <Alert variant="success">Подписанный приказ успешно загружен</Alert>
          )}
          {actionError && <Alert variant="danger">{actionError}</Alert>}

          <Card>
            <Card.Header>
              <Badge bg={getDayOffApplicationStatusBadgeVariant(application.status)}>
                {getDayOffApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>ID заявки</th>
                    <td>{application.id}</td>
                  </tr>
                  <tr>
                    <th>Тип выходного</th>
                    <td>
                      <Badge bg={getDayOffApplicationTypeBadgeVariant(application.type)}>
                        {getDayOffApplicationTypeLabel(application.type)}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Статус</th>
                    <td>
                      <Badge bg={getDayOffApplicationStatusBadgeVariant(application.status)}>
                        {getDayOffApplicationStatusLabel(application.status)}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Причина</th>
                    <td>{application.reason || '—'}</td>
                  </tr>
                  <tr>
                    <th>Заявление</th>
                    <td>
                      <Button
                        variant="primary"
                        onClick={handleDownloadFile}
                        disabled={downloadingFile}
                        size="sm"
                      >
                        {downloadingFile ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Загрузка...
                          </>
                        ) : (
                          'Скачать заявление'
                        )}
                      </Button>
                    </td>
                  </tr>
                  {dayOffStatusAtLeast(
                    application.status,
                    DayOffApplicationStatus.ORDER_SIGNING
                  ) && (
                    <tr>
                      <th>Приказ</th>
                      <td>
                        <Button
                          variant="outline-primary"
                          onClick={handleDownloadOrder}
                          disabled={downloadingOrder}
                          size="sm"
                        >
                          {downloadingOrder ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Загрузка...
                            </>
                          ) : (
                            'Скачать приказ'
                          )}
                        </Button>
                      </td>
                    </tr>
                  )}
                  {dayOffStatusAtLeast(
                    application.status,
                    DayOffApplicationStatus.ORDER_SIGNED
                  ) && (
                    <tr>
                      <th>Подписанный приказ</th>
                      <td>
                        <Button
                          variant="outline-primary"
                          onClick={handleDownloadSignedOrder}
                          disabled={downloadingSignedOrder}
                          size="sm"
                        >
                          {downloadingSignedOrder ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Загрузка...
                            </>
                          ) : (
                            'Скачать подписанный приказ'
                          )}
                        </Button>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <th>Дата создания</th>
                    <td>{new Date(application.created_at).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>

              {(application.status === DayOffApplicationStatus.ORDER_SIGNING ||
                application.status === DayOffApplicationStatus.ORDER_SIGNED) && (
                <>
                  <hr />
                  <h5>Загрузка подписанного приказа</h5>
                  {application.status === DayOffApplicationStatus.ORDER_SIGNED && (
                    <Alert variant="warning" className="py-2 px-3 small mb-2">
                      У заявки уже есть подписанный приказ. При загрузке нового подписанного приказа
                      существующий будет заменён.
                    </Alert>
                  )}
                  <p className="text-muted small">
                    Загрузите подписанный вами приказ, чтобы завершить процесс
                  </p>
                  <Form.Group>
                    <Form.Control
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const target = e.target as HTMLInputElement;
                        setSignedOrderFile(target.files?.[0] || null);
                      }}
                      disabled={uploadingSignedOrder}
                      size="sm"
                    />
                  </Form.Group>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-2"
                    onClick={handleUploadSignedOrder}
                    disabled={uploadingSignedOrder || !signedOrderFile}
                  >
                    {uploadingSignedOrder ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Загрузка...
                      </>
                    ) : (
                      'Загрузить подписанный приказ'
                    )}
                  </Button>
                </>
              )}

              {application.status === DayOffApplicationStatus.ORDER_SIGNED && (
                <>
                  <hr />
                  <h5>Решение по выходному</h5>
                  <p className="text-muted small">
                    Приказ подписан. Примите решение: подтвердить или отменить.
                  </p>
                  <div className="d-flex gap-2">
                    <Button variant="success" size="sm" onClick={() => setShowConfirmModal(true)}>
                      Подтвердить
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => setShowCancelModal(true)}>
                      Отменить
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Подтверждение выходного</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите подтвердить выходной? Это действие нельзя будет отменить.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
            disabled={actionLoading}
          >
            Отмена
          </Button>
          <Button variant="success" onClick={handleConfirm} disabled={actionLoading}>
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Отправка...
              </>
            ) : (
              'Подтвердить'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Отмена выходного</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите отменить выходной? Это действие нельзя будет отменить.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            disabled={actionLoading}
          >
            Назад
          </Button>
          <Button variant="danger" onClick={handleCancel} disabled={actionLoading}>
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Отправка...
              </>
            ) : (
              'Отменить выходной'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MyDayOffDetailPage;
